// friction-spin — composer interaction that OWNS the spinner's rendering (DECISION-051a).
//
// Mirrors the shelf-show-all "interaction owns rendering" pattern, extended to own GEOMETRY
// too (composedEntry.geometryOwned) so the composer skips its Body-only clone + slot-layout
// + populateComics for this fixture. The composer's box-family model doesn't fit the spinner
// (its rotating rack + 36 SLOT_RACK_* live under a separate SPIN_ROOT node, not under Body),
// so friction-spin instances the whole catalogue GLB, seats the curated rack comics under
// SPIN_ROOT at the baked SLOT poses, and installs stage.spinnerRack — reusing the verified
// spin.js wireSpin + the shared GLB/slot/comic helpers. Async (GLB load) — the composer
// awaits interaction wiring.

import { registerInteraction } from "./index.js";
import { wireSpin } from "./spin.js";
import { productsForSocket } from "../../_socket-transform.js";

async function wireFrictionSpin(ctx) {
	const { anchor, stage, composedEntry, fixtureCatalogue, token, productsAll } = ctx;
	if (!stage || !anchor || !composedEntry) return;

	// Instance the WHOLE rack GLB (base + SPIN_ROOT) at the anchor.
	const frame = await stage._instanceFixtureGlb(anchor, fixtureCatalogue, token);
	if (!frame || token !== stage.currentToken) return;
	composedEntry.geometryOwned = true;     // composer skips its Body-only clone for this kind
	composedEntry.worldGroup = frame;

	// Snapshot the frame's PRISTINE mesh order NOW — the composer's fixture material-library binding
	// (DECISION-053 Slice-2) uses numeric slot ids (spinner frame:[0,1,2]/header:[3]) that index into
	// this order; the comics seated below must not shift it.
	composedEntry.fixtureFrameMeshes = [];
	frame.traverse((n) => { if (n.isMesh) composedEntry.fixtureFrameMeshes.push(n); });

	const socket = fixtureCatalogue?.[anchor.fixtureKind]?.sockets?.[0];   // single RACK socket
	const socketId = socket?.id || "RACK";
	const type = (socket?.acceptedProductTypes || socket?.accepts || ["comic"])[0];
	if (type !== "comic") { composedEntry.productsRendered = true; return; }

	const slotNodes = stage._glbSlotNodes(frame, socketId);
	const rackProducts = productsForSocket(productsAll || [], anchor.name, socketId, type);
	// ASK-120 S3.5 — a rack comic carries its seat stamp like every other seated product, so the
	// spinner is selectable at seat grain too (it is a 12-pocket holder; per-pocket pinning is the
	// exact case the ≤12 listing rule was written for).
	const buildComic = (p) => stage._buildSocketComic(
		p, { x: 0, y: 0, z: 0, rotationY: 0, rotationX: 0, slot: 0 },
		stage._seatStampFor?.(anchor.name, p, socketId),
	);

	// Seat comics under SPIN_ROOT + install stage.spinnerRack (rotation/velocity/friction:4) —
	// the stage's per-frame spin + hover + hero machinery then drives it (manifest friction 4).
	wireSpin({ frame, slotNodes, products: rackProducts, stage, buildComic });
	// Report WHICH products were seated (wireSpin fills min(products, slotNodes)). A geometry-owning
	// interaction is the only one that knows; without it the composer's overflow signal has to guess,
	// and it used to guess "all of them are surplus" — a false capacity report relayed to GCG
	// (ASK-112 eyes-on, 2026-08-26).
	composedEntry.seatedProductIds = rackProducts.slice(0, Math.min(rackProducts.length, slotNodes.length)).map((p) => p?.id).filter((id) => id != null);
	composedEntry.productsRendered = true;
}

registerInteraction("friction-spin", wireFrictionSpin);
export { wireFrictionSpin };
